/** Console v0.5: Connections (design brief v0.5 s.6, the Connections mock-up), on session A's catalogue (#390,
 * contracts s.19) and today's Xero routes (connections list, disconnect one organisation, reconnect). Xero is
 * read-only; MYOB is "Coming soon" and can't be connected; the others take a register-interest. */

import { ApiError, apiRequest, disconnectXeroConnection, fetchXeroConnections, startXeroReconnect } from "../api.js";
import type { XeroConnectionRow } from "../api.js";
import type { ScreenContext } from "./app.js";
import { SCREENS } from "./app.js";
import { CONNECTIONS_COPY, connectorCard, isXeroSignIn } from "./connections-logic.js";
import type { Connector } from "./connections-logic.js";
import { shortDate } from "./team-logic.js";
import { button, card, emptyState, figure, h, legalFooter, loading, notice, openDialog, pageHead, sectionCard, table, tag } from "./ui.js";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function connections(ctx: ScreenContext): () => void {
  let catalogue: { connectors: Connector[]; can_manage: boolean } | null = null;
  let xero: XeroConnectionRow[] | null = null;
  let error: string | null = null;
  let xeroError: string | null = null;
  let flash: { text: string; ok: boolean } | null = null;
  let focusFlash = false;
  let gone = false;
  const canManageXero = (ctx.me as { can_manage_xero_connections?: boolean }).can_manage_xero_connections === true;

  async function load(fresh = false): Promise<void> {
    await Promise.all([
      apiRequest<{ connectors: Connector[]; can_manage: boolean }>("GET", "/api/connections", "Preekie couldn't load your connections.")
        .then((c) => {
          catalogue = c;
          error = null;
        })
        .catch((err: unknown) => {
          error = messageOf(err, "Preekie couldn't load your connections. Try again in a few minutes.");
        }),
      fetchXeroConnections(fresh)
        .then((r) => {
          xero = r.connections;
          xeroError = null;
        })
        .catch((err: unknown) => {
          xero = [];
          xeroError = messageOf(err, "Preekie couldn't load your Xero connections.");
        }),
    ]);
    draw();
  }

  async function registerInterest(c: Connector): Promise<void> {
    try {
      await apiRequest("POST", "/api/connections/interest", "Your interest wasn't recorded.", { connector: c.id });
      flash = { text: c.status === "coming_soon" ? `You're on the list for ${c.name}.` : `Interest in ${c.name} recorded. Thank you.`, ok: true };
    } catch (err) {
      flash = { text: messageOf(err, "Your interest wasn't recorded. Try again in a few minutes."), ok: false };
    }
    focusFlash = true;
    await load();
  }

  function disconnect(row: XeroConnectionRow): void {
    const org = row.organisation_name ?? row.client_name;
    openDialog(
      `Disconnect ${org}?`,
      [
        h("p", null, `Preekie will stop reading ${row.client_name}'s Xero data. Nothing inside Xero changes and nothing is deleted. Preekie can only read it again once the organisation is reconnected.`),
        h("p", "pk5-muted", "Only this organisation is disconnected. Other organisations stay connected."),
      ],
      [
        { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
        {
          label: `Disconnect ${org}`,
          variant: "destructive",
          onClick: async (close, btn) => {
            btn.disabled = true;
            try {
              const r = await disconnectXeroConnection(row.client_slug, row.connection_id ?? "");
              flash = { text: r.message, ok: true };
            } catch (err) {
              flash = { text: messageOf(err, "Preekie couldn't disconnect this organisation, so it's still connected."), ok: false };
            }
            focusFlash = true;
            close();
            await load(true);
          },
        },
      ]
    );
  }

  async function reconnect(row: XeroConnectionRow): Promise<void> {
    try {
      const r = await startXeroReconnect(row.client_slug);
      // D: only ever link to Xero's own sign-in.
      if (!isXeroSignIn(r.authorization_url)) {
        flash = { text: "Preekie couldn't start reconnecting this organisation. Nothing changed.", ok: false };
        focusFlash = true;
        return draw();
      }
      const open = h("a", "pk5-btn pk5-btn-primary", "Open Xero");
      open.href = r.authorization_url;
      open.target = "_blank";
      open.rel = "noopener";
      openDialog(
        `Reconnect ${row.client_name}`,
        [
          h("p", null, `Preekie needs fresh permission from someone who can sign in to ${row.client_name}'s organisation in Xero. In Xero, choose the same organisation as before; Preekie won't accept a different one.`),
          h("p", "pk5-muted", "The link works once, until ", figure(shortDate(r.expires_at, ctx.timeZone)), "."),
          open,
        ],
        [{ label: "Done", variant: "secondary", safe: true, onClick: (close) => void close() }]
      );
    } catch (err) {
      flash = { text: messageOf(err, "Preekie couldn't start reconnecting this organisation. Nothing changed."), ok: false };
      focusFlash = true;
      draw();
    }
  }

  function draw(): void {
    if (gone) return;
    const parts: Node[] = [pageHead("Connections", CONNECTIONS_COPY.intro)];
    if (flash) {
      const n = notice(flash.text, flash.ok ? "status" : "alert");
      n.tabIndex = -1;
      n.classList.add("pk5-flash");
      parts.push(n);
    }
    if (error) parts.push(notice(error, "alert"));
    else if (!catalogue) parts.push(loading());
    else {
      const cat = catalogue;
      const grid = h("div", "pk5-service-grid");
      for (const c of cat.connectors) {
        const v = connectorCard(c, cat.can_manage);
        const act = v.action ? button(v.action, "secondary", () => void registerInterest(c)) : null;
        if (act) act.setAttribute("aria-label", `${v.action}: ${c.name}`);
        grid.append(
          h(
            "article",
            `pk5-service${c.id === "xero" && (c.connected_organisations ?? 0) > 0 ? " pk5-connector-on" : ""}`,
            h("div", "pk5-agent-head", h("h3", "pk5-service-name", c.name), tag(v.badge, v.tone)),
            h("p", "pk5-service-line", v.detail),
            act
          )
        );
      }
      parts.push(sectionCard("Accounting systems", [grid]));
    }

    const rows = (xero ?? []).map((r) => {
      const status =
        r.status === "connected"
          ? tag(r.status_label, "good")
          : h("span", "pk5-person-text", h("span", "pk5-chip pk5-chip-exception", r.status_label), r.message ? h("span", "pk5-muted", r.message) : null);
      const acts = h("span", "pk5-invite-actions");
      if (canManageXero && r.can_reconnect) {
        const b = button("Reconnect", "secondary", () => void reconnect(r));
        b.setAttribute("aria-label", `Reconnect ${r.client_name}`);
        acts.append(b);
      }
      if (canManageXero && r.connection_id) {
        const b = button("Disconnect", "destructive", () => disconnect(r));
        b.setAttribute("aria-label", `Disconnect ${r.organisation_name ?? r.client_name}`);
        acts.append(b);
      }
      return [r.organisation_name ?? "–", r.client_name, "Read-only", status, figure(shortDate(r.checked_at, ctx.timeZone)), acts];
    });
    parts.push(
      sectionCard(CONNECTIONS_COPY.xeroTitle, [
        h("p", "pk5-muted", CONNECTIONS_COPY.xeroNote),
        xeroError
          ? notice(xeroError, "alert")
          : xero === null
            ? loading()
            : rows.length
              ? table(CONNECTIONS_COPY.xeroTitle, [{ label: "Organisation" }, { label: "Linked client" }, { label: "Access" }, { label: "Status" }, { label: "Last checked" }, { label: "Actions" }], rows)
              : emptyState("No Xero organisations connected yet."),
      ])
    );

    const mail = h("a", "pk5-link", "Request a connection");
    mail.href = "mailto:hello@preekie.com?subject=Connection%20request";
    parts.push(
      sectionCard("Other connections", [
        h(
          "div",
          "pk5-service-grid",
          card(undefined, h("h3", "pk5-service-name", "Bank feeds"), h("p", "pk5-service-line", CONNECTIONS_COPY.bankFeeds)),
          card(undefined, h("h3", "pk5-service-name", "ATO lodgement"), h("p", "pk5-service-line", CONNECTIONS_COPY.ato)),
          card(undefined, h("h3", "pk5-service-name", "Need another system?"), h("p", "pk5-service-line", CONNECTIONS_COPY.another), mail)
        ),
      ]),
      legalFooter()
    );
    ctx.root.replaceChildren(...parts);
    if (focusFlash) {
      focusFlash = false;
      (ctx.root.querySelector(".pk5-flash") as HTMLElement | null)?.focus();
    }
  }

  draw();
  void load();
  return () => {
    gone = true;
  };
}

SCREENS.connections = connections;
